import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getNovelById, createNovel, updateNovel } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, Save } from 'lucide-react';

const NovelEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = id && id !== 'new';

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'in-progress'
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      fetchNovel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchNovel = async () => {
    try {
      const data = await getNovelById(id);
      setFormData({
        title: data.title,
        description: data.description,
        status: data.status
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load novel",
        variant: "destructive"
      });
      navigate('/admin/novels');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEditing) {
        await updateNovel(id, formData);
        toast({
          title: "Novel updated",
          description: "Your changes have been saved."
        });
      } else {
        const newNovel = await createNovel(formData);
        toast({
          title: "Novel created",
          description: "Your novel has been created. Add chapters now!"
        });
        navigate(`/admin/novel/chapters/${newNovel.id}`);
        return;
      }
      navigate('/admin/novels');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save novel.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/admin/novels" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={16} />Back to Novels
          </Link>
          <h1 className="text-2xl font-serif text-gray-900">
            {isEditing ? 'Edit Novel' : 'Create New Novel'}
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 p-8 space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
              Title *
            </label>
            <Input type="text" id="title" name="title" value={formData.title} onChange={handleChange} required placeholder="Enter novel title" />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
              Description *
            </label>
            <Textarea id="description" name="description" value={formData.description} onChange={handleChange} required rows={4} placeholder="Brief description or synopsis" />
          </div>

          <div>
            <label htmlFor="status" className="block text-sm uppercase tracking-wider text-gray-700 mb-2">
              Status *
            </label>
            <select id="status" name="status" value={formData.status} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900">
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="flex items-center gap-4 pt-6 border-t border-gray-200">
            <Button type="submit" disabled={saving} className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800">
              <Save size={16} />
              {saving ? 'Saving...' : (isEditing ? 'Update Novel' : 'Create Novel')}
            </Button>
            <Link to="/admin/novels">
              <Button type="button" variant="outline">Cancel</Button>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NovelEditor;